import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Redirect } from 'react-router-dom/cjs/react-router-dom.min';
import { PATHS } from 'AppPaths';
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset as authConfirmPasswordReset,
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail as authSendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as authSignOut,
  TwitterAuthProvider,
  updateEmail as authUpdateEmail,
  updatePassword as authUpdatePassword,
  updateProfile as authUpdateProfile,
} from 'firebase/auth';
import useCheckSub from 'hooks/useCheckSub';
import { useAtom } from 'jotai';
import PaywallExplanationModal from 'pages/paywall/PaywallExplanationModal';
import queryString from 'query-string';
import { isDrawerOpenAtom, limAndUsageInfoAtom } from 'store';

import PageLoader from '../components/PageLoader';

import analytics from './analytics';
import { deleteCookie, setCookie } from './cookies';
import {
  createCollectionWIthCustomIdTJ,
  createUser,
  updateEmailVerifed,
  updateUser,
  useUser,
} from './db';
import { firebaseApp } from './firebase';
import {
  checkAndAssignLicenseSeat,
  checkLicenseKey,
  createToken,
  getUserLimitsAndUsage,
  verifyUserEmail,
} from './firebasefunctions';
import { history } from './router';
import { CustomError } from './util';
// Whether to merge extra user data from database into `auth.user`
const MERGE_DB_USER = true;
// Whether to send email verification on signup
const EMAIL_VERIFICATION = true;
// Whether to connect analytics session to `user.uid`
const ANALYTICS_IDENTIFY = true;
// Initialize Firebase auth
const auth = getAuth(firebaseApp);

// Create a `useAuth` hook and `AuthProvider` that enables
// any component to subscribe to auth and re-render when it changes.
const authContext = createContext();
export const useAuth = () => useContext(authContext);
// This should wrap the app in `src/pages/_app.js`
export function AuthProvider({ children }) {
  const auth = useAuthProvider();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook that creates the `auth` object and handles state
// This is called from `AuthProvider` above (extracted out for readability)
function useAuthProvider() {
  const [, setIsDrawerOpen] = useAtom(isDrawerOpenAtom);
  const [, setLimAndUsageInfo] = useAtom(limAndUsageInfoAtom);
  // Store auth user in state
  // `user` will be object, `null` (loading) or `false` (logged out)
  const [user, setUser] = useState(null);
  // Merge extra user data from the database
  // This means extra user data (such as payment plan) is available as part
  // of `auth.user` and doesn't need to be fetched separately. Convenient!
  let finalUser = useMergeExtraData(user, { enabled: MERGE_DB_USER });
  // Add custom fields and formatting to the `user` object
  finalUser = useFormatUser(finalUser);

  useCheckSub(user);
  // Connect analytics session to user
  //TJANAL
  useIdentifyUser(finalUser, { enabled: ANALYTICS_IDENTIFY });

  // Handle response from auth functions (`signup`, `signin`, and `signinWithProvider`)
  const handleAuth = async (
    response,
    provider = false,
    isEnterprise = false
  ) => {
    const { user } = response;
    const { isNewUser } = getAdditionalUserInfo(response);

    // Ensure Firebase user is ready before we continue
    await waitForFirebase(user.uid);

    // Create the user in the database if they are new
    if (isNewUser) {
      await createUser(user.uid, {
        email: user.email,
        ...(user.photoURL && { photo_url: user.photoURL }),
      });

      await createCollectionWIthCustomIdTJ(
        {
          owner: user.uid,
          title: 'Unsorted',
        },
        'Unsorted'
      );

      // Send email verification if enabled, not a provider sign-in, and NOT an enterprise signup
      if (EMAIL_VERIFICATION && !provider && !isEnterprise) {
        sendEmailVerification(auth.currentUser);
      }
    }

    // Update user in state
    setUser(user);
    return user;
  };

  const signup = (email, password) => {
    return createUserWithEmailAndPassword(auth, email, password).then(
      async (userCredential) => {
        localStorage.setItem('isBannerOpen', true);
        await handleAuth(userCredential);

        return userCredential.user;
      }
    );
  };

  const signin = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password).then(
      (handleAuth) => {
        if (handleAuth.user.emailVerified) {
          return handleAuth;
        } else {
          const message = 'Your email address is not verified';
          throw new CustomError(401, message);
        }
      }
    );
  };

  const signinWithProvider = (name) => {
    // Get provider by name ("google", "facebook", etc)
    const provider = authProviders.find((p) => p.name === name).get();
    return signInWithPopup(auth, provider).then(async (user) => {
      await handleAuth(user, name);
      return user;
    });
  };

  const signout = () => {
    setIsDrawerOpen(false);
    localStorage.clear();
    deleteCookie('auth_token');
    history.replace(PATHS.AUTH('signin'));
    return authSignOut(auth);
  };

  const sendPasswordResetEmail = (email) => {
    return authSendPasswordResetEmail(auth, email);
  };

  const confirmPasswordReset = (password, code) => {
    // Get code from query string object
    const resetCode = code || getFromQueryString('oobCode');
    return authConfirmPasswordReset(auth, resetCode, password);
  };

  const updatePassword = (password) => {
    return authUpdatePassword(auth.currentUser, password);
  };

  // const updateEmailVerifed = async () => {
  //   await getAuth().updateUser(user.uid, { emailVerified: true });
  // };

  // Update auth user and persist data to database
  // Call this function instead of multiple auth/db update functions
  const updateProfile = async (data) => {
    const { email, name, picture } = data;

    // Update auth email
    if (email) {
      await authUpdateEmail(auth.currentUser, email);
    }

    // Update built-in auth profile fields
    // These fields are renamed in `useFormatUser`, so when updating we
    // need to make sure to use their original names (`displayName`, `photoURL`, etc)
    if (name || picture) {
      let fields = {};
      if (name) fields.displayName = name;
      if (picture) fields.photoURL = picture;
      await authUpdateProfile(auth.currentUser, fields);
    }

    // Persist all data to the database
    await updateUser(user.uid, data);

    // Update user in state
    setUser(auth.currentUser);
  };

  const handleReload = async () => {
    setUser(null);
    await auth.currentUser.reload();
    setUser(auth.currentUser);
  };

  const enterprise = async (email, password, licenseKey) => {
    // 1. Input Data Validation
    if (!email || !password || !licenseKey) {
      console.warn('Missing email, password, or licenseKey');
      throw new CustomError(
        400,
        'Missing required fields (email, password, licenseKey)'
      ); // Bad Request
    }

    try {
      // 2. License Key Verification
      const licenseCheckResponse = await checkLicenseKey({
        licenseKey: licenseKey,
      });

      if (!licenseCheckResponse.data || !licenseCheckResponse.data.isValid) {
        throw new CustomError(401, 'Invalid license key');
      }

      // 3. User Creation
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password
        );
        localStorage.setItem('isBannerOpen', true);
        // Note the `isEnterprise = true` here.
        await handleAuth(userCredential, false, true);
      } catch (createUserError) {
        throw new CustomError(
          500,
          'Failed to create user account',
          createUserError
        );
      }

      // 4. Analytics (Track event after successful user creation)
      analytics.track('Enterprise Signup', { licenseKey });

      // 5. Verification email
      await verifyUserEmail({ licenseKey: licenseKey });

      // 6. License Assignment
      await checkAndAssignLicenseSeat({ licenseKey: licenseKey });

      // Load limits and usage on initial auth as well
      try {
        const limitsAndUsage = await getUserLimitsAndUsage();

        if (limitsAndUsage?.data) {
          setLimAndUsageInfo((prev) => ({
            ...prev,
            entitlement: limitsAndUsage.data.entitlement,
            maxCollections: limitsAndUsage.data.maxCollections,
            maxQuestions: limitsAndUsage.data.maxQuestions,
            maxQuestionsResetTime: limitsAndUsage.data.maxQuestionsResetTime,
            success: limitsAndUsage.data.success,
            usedCollections: limitsAndUsage.data.usedCollections,
            usedQuestions: limitsAndUsage.data.usedQuestions,
          }));
        } else {
          console.warn(
            'No limits data returned from getUserLimitsAndUsage on initial auth'
          );
        }
      } catch (error) {
        console.error(
          'Error fetching user limits and usage on initial auth:',
          error
        );
      }

      // 7. Return User Data
      return userCredential.user;
    } catch (error) {
      if (!(error instanceof CustomError)) {
        throw new CustomError(500, error);
      }
      throw error;
    }
  };

  useEffect(() => {
    // Subscribe to user on mount
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);

        const token = await createToken(user.uid);

        setCookie('auth_token', token.data, {
          SameSite: 'Strict',
        });
      } else {
        setUser(false);
        setLimAndUsageInfo(null);
      }
    });

    // Unsubscribe on cleanup
    return () => unsubscribe();
  }, []);

  return {
    user: finalUser,
    signup,
    signin,
    signinWithProvider,
    signout,
    enterprise,
    sendPasswordResetEmail,
    confirmPasswordReset,
    updatePassword,
    updateProfile,
    handleReload,
  };
}

function useFormatUser(user) {
  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // Return if auth user is `null` (loading) or `false` (not authenticated)
    if (!user) return user;

    // Create an array of user's auth providers by id (["password", "google", etc])
    // Components can read this to prompt user to re-auth with the correct provider
    const providers = user.providerData.map(({ providerId }) => {
      return authProviders.find((p) => p.id === providerId).name;
    });

    return {
      // Include full auth user data
      ...user,
      // Alter the names of some fields
      name: user.displayName,
      picture: user.photoURL,
      // User's auth providers
      providers: providers,

      // Add `planIsActive: true` if subscription status is active or trialing
      planIsActive: ['active', 'trialing'].includes(
        user.stripeSubscriptionStatus
      ),
    };
  }, [user]);
}

function useMergeExtraData(user, { enabled }) {
  // Get extra user data from database
  const { data, status, error } = useUser(enabled && user && user.uid);

  // Memoize so returned object has a stable identity
  return useMemo(() => {
    // If disabled or no auth user (yet) then just return
    if (!enabled || !user) return user;

    switch (status) {
      case 'success':
        // If successful, but `data` is `null`, that means user just signed up and the `createUser`
        // function hasn't populated the db yet. Return `null` to indicate auth is still loading.
        // The above call to `useUser` will re-render things once the data comes in.
        if (data === null) return null;
        // If the user registers but has not yet confirmed his email
        return { ...user, ...data };
      case 'error':
        // Uh oh.. Let's at least show a helpful error.
        throw new Error(`
          Error: ${error.message}
          This happened while attempting to fetch extra user data from the database
          to include with the authenticated user. Make sure the database is setup or
          disable merging extra user data by setting MERGE_DB_USER to false.
        `);
      default:
        // We have an `idle` or `loading` status so return `null`
        // to indicate that auth is still loading.
        return null;
    }
  }, [user, enabled, data, status, error]);
}

// Connect analytics session to current user
//TJANAL
function useIdentifyUser(user, { enabled }) {
  useEffect(() => {
    if (enabled && user) {
      analytics.identify(user.uid);
    }
  }, [user, enabled]);
}

// A Higher Order Component for requiring authentication
export const requireAuth = (Component) => {
  return function RequireAuthHOC(props) {
    // Get authenticated user
    const auth = useAuth();

    useEffect(() => {
      // Redirect if not signed in
      if (auth.user === false) {
        history.replace(PATHS.AUTH('signin'));
      }
    }, [auth]);

    // Show loading indicator
    // We're either loading (user is `null`) or about to redirect from above `useEffect` (user is `false`)
    if (!auth.user) {
      return <PageLoader />;
    }

    // Render component now that we have user
    return <Component {...props} />;
  };
};

export const requireAuthStatus = (Component) => {
  return function RequireAuthStatusHOC(props) {
    // Get authenticated user
    const auth = useAuth();

    if (auth.user === null) {
      return <PageLoader />;
    } else if (auth.user) {
      return <Redirect to={PATHS.COLLECTIONS} />;
    }

    // Render component now that we have user
    return <Component {...props} />;
  };
};

export const requireSub = (Component) => {
  return function RequireSubHOC(props) {
    const auth = useAuth();
    const [limAndUsageInfo] = useAtom(limAndUsageInfoAtom);

    useEffect(() => {
      if (!auth.user) {
        return; // Wait for user to authenticate
      }

      if (!limAndUsageInfo) {
        return; // Wait for limits and usage info to load
      }

      const {
        entitlement,
        maxCollections,
        usedCollections,
        maxQuestions,
        usedQuestions,
      } = limAndUsageInfo;
    }, [auth.user, limAndUsageInfo, history]);

    if (!auth.user || !limAndUsageInfo) {
      return <PageLoader />; // Show loading indicator
    }

    return <Component {...props} />;
  };
};

// Handle Firebase email link for reverting to original email after an email change
export const handleRecoverEmail = async (code) => {
  // Check that action code is valid
  const info = await checkActionCode(auth, code);
  // Revert to original email by applying action code
  await applyActionCode(auth, code);
  // Send password reset email so user can change their password in the case
  // that someone else got into their account and changed their email.
  await authSendPasswordResetEmail(auth, info.data.email);
  // Return original email so it can be displayed by calling component
  return info.data.email;
};

// Handle Firebase email link for verifying email
export const handleVerifyEmail = (code) => {
  return applyActionCode(auth, code);
};

const authProviders = [
  {
    id: 'apple.com',
    name: 'apple',
    get: () => new OAuthProvider('apple.com'),
  },
  {
    id: 'password',
    name: 'password',
  },
  {
    id: 'google.com',
    name: 'google',
    get: () => new GoogleAuthProvider(),
  },
  {
    id: 'facebook.com',
    name: 'facebook',
    get: () => {
      const provider = new FacebookAuthProvider();
      provider.setCustomParameters({ display: 'popup' });
      return provider;
    },
  },
  {
    id: 'twitter.com',
    name: 'twitter',
    get: () => new TwitterAuthProvider(),
  },
  {
    id: 'github.com',
    name: 'github',
    get: () => new GithubAuthProvider(),
  },
];

// Wait for Firebase user to be initialized before resolving promise
// and taking any further action (such as writing to the database)
const waitForFirebase = (uid) => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Ensure we have a user with expected `uid`
      if (user && user.uid === uid) {
        resolve(user); // Resolve promise
        unsubscribe(); // Prevent from firing again
      }
    });
  });
};

const getFromQueryString = (key) => {
  return queryString.parse(window.location.search)[key];
};
